// {project}/src/wasmJsMain/resources/deviceUtils.mjs

/**
 * Gets the number of logical processors available
 * @returns {number} Number of logical processors, defaults to 4 if not available
 */
export function getHardwareConcurrency() {
    return navigator.hardwareConcurrency || 4;
}

/**
 * Gets the approximate amount of device memory in gigabytes
 * @returns {number|null} Device memory in GB, or null if not available (Firefox, Safari)
 */
export function getDeviceMemory() {
    return navigator.deviceMemory || null;
}

/**
 * Gets the user agent string
 * @returns {string} Browser user agent
 */
export function getUserAgent() {
    return navigator.userAgent;
}

/**
 * Detects device performance mode based on available hardware
 * Default to LOW for web, only mark as HIGH for powerful devices
 *
 * NOTE: navigator.deviceMemory is capped at 8GB in most browsers for privacy,
 * even if the device has more RAM. We rely more on CPU cores for detection.
 *
 * @returns {string} 'LOW' or 'HIGH'
 */
export function detectDevicePerformanceMode() {
    const deviceMemory = getDeviceMemory();
    const hardwareConcurrency = getHardwareConcurrency();

    // Default to LOW for web, only mark as HIGH for powerful devices:
    // HIGH performance devices have:
    // - 8GB+ RAM (max reported by browser) with 6+ cores (powerful laptops/desktops)
    // - OR 8+ cores regardless of memory (powerful devices)
    // - OR 4GB-8GB RAM with 8+ cores

    // Note: deviceMemory is capped at 8GB by browsers for privacy,
    // so we use CPU cores as the primary indicator
    const hasManyCores = hardwareConcurrency >= 8;
    const hasDecentCoresWithGoodMemory = hardwareConcurrency >= 6 && deviceMemory !== null && deviceMemory >= 8.0;
    const hasDecentCoresWithOkMemory = hardwareConcurrency >= 8 && deviceMemory !== null && deviceMemory >= 4.0;

    const isPowerfulDevice = hasManyCores || hasDecentCoresWithGoodMemory || hasDecentCoresWithOkMemory;

    return isPowerfulDevice ? "HIGH" : "LOW";
}
